import React, { useState } from 'react';
import { convertPrice } from '../../services/adminApi';
import type { PriceFields } from './price';
import styles from './AdminCrud.module.css';

interface PriceInputProps {
  value: PriceFields;
  onChange: (value: PriceFields) => void;
}

const CONVERTED_CURRENCIES: { key: keyof PriceFields; label: string }[] = [
  { key: 'eu', label: 'EUR' },
  { key: 'kzt', label: 'KZT' },
];

/**
 * USD is the only price the admin fills in. "Пересчитать" sends it to
 * /api/admin/convert-price and fills EUR/KZT, which stay editable behind the
 * "показать" toggle. Saving without pressing it converts server-side anyway.
 */
const PriceInput: React.FC<PriceInputProps> = ({ value, onChange }) => {
  const [showConverted, setShowConverted] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handle_change = (key: keyof PriceFields, amount: string) => {
    onChange({ ...value, [key]: amount });
  };

  const handle_convert = async () => {
    setError(null);
    setNotice(null);
    setIsConverting(true);

    try {
      const result = await convertPrice(Number(value.us));

      onChange({
        ...value,
        eu: String(result.price.eu),
        kzt: String(result.price.kzt),
      });
      setShowConverted(true);

      if (result.source === 'fallback') {
        setNotice('Курс валют недоступен — использован резервный курс');
      }
    } catch (err) {
      setError((err as Error).message || 'Не удалось пересчитать');
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div className={styles.field}>
      <span className={styles.field_label}>Price (USD)</span>
      <input
        className={styles.input}
        type="number"
        min={0}
        step="any"
        value={value.us}
        onChange={(event) => handle_change('us', event.target.value)}
      />

      <div className={styles.translate_row}>
        <button
          type="button"
          className={`${styles.button_secondary} ${styles.button_small}`}
          onClick={handle_convert}
          disabled={isConverting || Number(value.us) <= 0}
        >
          {isConverting ? 'Пересчёт…' : 'Пересчитать'}
        </button>
        <button
          type="button"
          className={styles.toggle_link}
          onClick={() => setShowConverted((shown) => !shown)}
        >
          {showConverted ? 'Скрыть EUR / KZT' : 'Показать EUR / KZT'}
        </button>
      </div>

      {error && <p className={styles.error_text}>{error}</p>}
      {notice && <p className={styles.error_text}>{notice}</p>}

      {showConverted && (
        <div className={styles.localized_group}>
          {CONVERTED_CURRENCIES.map(({ key, label }) => (
            <div key={key} className={styles.field}>
              <span className={styles.field_label}>{label}</span>
              <input
                className={styles.input}
                type="number"
                min={0}
                step="any"
                placeholder={label}
                value={value[key]}
                onChange={(event) => handle_change(key, event.target.value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PriceInput;
